import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { destroyReactComponent } from '@/react-bridge';
import { CancelSubscriptionModal } from '@/components/modals/CancelSubscriptionModal';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { hasValue, hasStringValue } from '@/utils/legacy-utils';

// ── Global Declarations ──────────────────────────────────────────────

import { getAuthObject } from '@/utils/auth';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const openWebpage = _wg('openWebpage');
const isoDatetimeToFormattedDatetime = _wg('isoDatetimeToFormattedDatetime');

// ============================================================================
// Module-level State
// ============================================================================

let paymentManagementContainer: HTMLElement | null = null;
let cardElement: any = null;
let stripe: any = null;

// ============================================================================
// Currency Utilities
// ============================================================================

// Based on https://www.currency-iso.org/dam/downloads/lists/list_one.xml, parsed on 2020-06-04.
const currencyCodeToUnits: Record<string, number> = {
  AED: 2, AFN: 2, ALL: 2, AMD: 2, ANG: 2, AOA: 2, ARS: 2, AUD: 2, AWG: 2, AZN: 2,
  BAM: 2, BBD: 2, BDT: 2, BGN: 2, BHD: 3, BIF: 0, BMD: 2, BND: 2, BOB: 2, BOV: 2,
  BRL: 2, BSD: 2, BTN: 2, BWP: 2, BYN: 2, BZD: 2, CAD: 2, CDF: 2, CHE: 2, CHF: 2,
  CHW: 2, CLF: 4, CLP: 0, CNY: 2, COP: 2, COU: 2, CRC: 2, CUC: 2, CUP: 2, CVE: 2,
  CZK: 2, DJF: 0, DKK: 2, DOP: 2, DZD: 2, EGP: 2, ERN: 2, ETB: 2, EUR: 2, FJD: 2,
  FKP: 2, GBP: 2, GEL: 2, GHS: 2, GIP: 2, GMD: 2, GNF: 0, GTQ: 2, GYD: 2, HKD: 2,
  HNL: 2, HRK: 2, HTG: 2, HUF: 2, IDR: 2, ILS: 2, INR: 2, IQD: 3, IRR: 2, ISK: 0,
  JMD: 2, JOD: 3, JPY: 0, KES: 2, KGS: 2, KHR: 2, KMF: 0, KPW: 2, KRW: 0, KWD: 3,
  KYD: 2, KZT: 2, LAK: 2, LBP: 2, LKR: 2, LRD: 2, LSL: 2, LYD: 3, MAD: 2, MDL: 2,
  MGA: 2, MKD: 2, MMK: 2, MNT: 2, MOP: 2, MRU: 2, MUR: 2, MVR: 2, MWK: 2, MXN: 2,
  MXV: 2, MYR: 2, MZN: 2, NAD: 2, NGN: 2, NIO: 2, NOK: 2, NPR: 2, NZD: 2, OMR: 3,
  PAB: 2, PEN: 2, PGK: 2, PHP: 2, PKR: 2, PLN: 2, PYG: 0, QAR: 2, RON: 2, RSD: 2,
  RUB: 2, RWF: 0, SAR: 2, SBD: 2, SCR: 2, SDG: 2, SEK: 2, SGD: 2, SHP: 2, SLL: 2,
  SOS: 2, SRD: 2, SSP: 2, STN: 2, SVC: 2, SYP: 2, SZL: 2, THB: 2, TJS: 2, TMT: 2,
  TND: 3, TOP: 2, TRY: 2, TTD: 2, TWD: 2, TZS: 2, UAH: 2, UGX: 0, USD: 2, USN: 2,
  UYI: 0, UYU: 2, UYW: 4, UZS: 2, VES: 2, VND: 0, VUV: 0, WST: 2, XAF: 0, XCD: 2,
  XOF: 0, XPF: 0, YER: 2, ZAR: 2, ZMW: 2, ZWL: 2,
};

function getStripePriceObjectFormattedCurrency(stripePriceObject: { balance: number; currency: string }): string | null {
  if (hasValue(stripePriceObject) && hasValue(stripePriceObject.currency)) {
    const currencyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: stripePriceObject.currency,
    });
    const currencyUnits = currencyCodeToUnits[stripePriceObject.currency.toUpperCase()] || 0;
    const balance = stripePriceObject.balance * Math.pow(10, -currencyUnits);
    return currencyFormatter.format(balance);
  }
  return null;
}

// ============================================================================
// Stripe Card Update Modal Handlers
// ============================================================================

function showCardPaymentMethodUpdateDialog(): void {
  const modal = document.getElementById('cardPaymentMethodUpdateDialog');
  if (modal) {
    modal.style.display = 'flex';
    document.body.classList.add('kstc-scroll-lock');
  }
}

function hideCardPaymentMethodUpdateDialog(): void {
  const modal = document.getElementById('cardPaymentMethodUpdateDialog');
  if (modal) {
    modal.style.display = 'none';
    document.body.classList.remove('kstc-scroll-lock');
    if (cardElement) {
      cardElement.unmount();
      cardElement = null;
    }
    const cardholderInput = document.getElementById('cardholder-name') as HTMLInputElement;
    if (cardholderInput) {
      cardholderInput.value = '';
    }
    const cardErrors = document.getElementById('card-errors');
    if (cardErrors) {
      cardErrors.textContent = '';
    }
  }
}

function setPaymentMethodUpdateDialogEnabled(isEnabled: boolean): void {
  const submitBtn = document.getElementById('btnCardPaymentMethodUpdateDialogSubmit') as HTMLButtonElement;
  const cancelBtn = document.getElementById('btnCardPaymentMethodUpdateDialogCancel') as HTMLButtonElement;
  if (submitBtn) submitBtn.disabled = !isEnabled;
  if (cancelBtn) cancelBtn.disabled = !isEnabled;
}

// Stripe throws an error when the html for the card payment is in an iframe, so use a dialog
// These handlers work with the modal in index.html
document.addEventListener('click', function (e) {
  const target = e.target as HTMLElement;

  // Open modal
  if (target.id === 'showCardPaymentMethodUpdateDialog') {
    showCardPaymentMethodUpdateDialog();

    const authTokenObj = getAuthObject();
    if (!hasValue(authTokenObj)) {
      App.showPopupNotification('Error getting information to contact payment processor. Please contact Kerkton support.', 'error');
      return;
    }

    // Create a Stripe client
    stripe = (window as any).Stripe(authTokenObj.stripePublishKey);
    const elements = stripe.elements();

    // Custom styling for Stripe Element
    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    };

    // Create and mount card Element
    cardElement = elements.create('card', { style: style });
    cardElement.mount('#card-element');
    cardElement.addEventListener('change', function (event: any) {
      const displayError = document.getElementById('card-errors');
      if (displayError) {
        displayError.textContent = event.error ? event.error.message : '';
      }
    });
  }

  // Close modal (X button or Cancel button)
  if (target.id === 'btnCardPaymentMethodUpdateDialogClose' || target.id === 'btnCardPaymentMethodUpdateDialogCancel') {
    hideCardPaymentMethodUpdateDialog();
  }

  // Submit
  if (target.id === 'btnCardPaymentMethodUpdateDialogSubmit') {
    const cardholderInput = document.getElementById('cardholder-name') as HTMLInputElement;
    const cardholderName = cardholderInput ? cardholderInput.value : '';

    if (!hasStringValue(cardholderName)) {
      App.showPopupNotification("Please enter the card holder's name.", 'error');
      return;
    }

    setPaymentMethodUpdateDialogEnabled(false);
    const attachToCurrentCompany = true;

    api.getSetupIntentClientSecret(
      attachToCurrentCompany,
      (result: any) => {
        if (result.clientSecret === null) {
          setPaymentMethodUpdateDialogEnabled(true);
          App.showPopupNotification('Unable to update card. Please contact Kerkton support to ensure you are set up to handle automatic payments.', 'error');
          return;
        }

        stripe
          .confirmCardSetup(result.clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: cardholderName,
              },
            },
          })
          .then(function (result: any) {
            if (result.error) {
              setPaymentMethodUpdateDialogEnabled(true);
              App.showPopupNotification('Error updating card.', 'error');
            } else {
              api.updateDefaultCardPaymentMethod(
                { paymentMethod: result.setupIntent.payment_method },
                (_result: any) => {
                  setPaymentMethodUpdateDialogEnabled(true);
                  hideCardPaymentMethodUpdateDialog();
                  location.reload();
                },
                (_errorResult: any) => {
                  setPaymentMethodUpdateDialogEnabled(true);
                  App.showPopupNotification('Error setting card as default card.', 'error');
                }
              );
            }
          });
      },
      (_errorResult: any) => {
        setPaymentMethodUpdateDialogEnabled(true);
        App.showPopupNotification('Error getting token to update card.', 'error');
      }
    );
  }
});

// ── Component ────────────────────────────────────────────────────────

export function PaymentManagementScreen() {
  const { theme } = useTheme();

  const [isLoading, setIsLoading] = useState(true);
  const [lastFourDigits, setLastFourDigits] = useState<string | null>(null);
  const [customerBalance, setCustomerBalance] = useState<string | null>(null);
  const [upcomingInvoiceBalance, setUpcomingInvoiceBalance] = useState<string | null>(null);
  const [subscriptionExpiration, setSubscriptionExpiration] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean | null>(null);
  const [lastInvoiceUnpaidHostedUrl, setLastInvoiceUnpaidHostedUrl] = useState('');
  const [subscriptionExpirationRaw, setSubscriptionExpirationRaw] = useState('');
  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] = useState(false);

  // Load payment management info on mount
  useEffect(() => {
    api.getPaymentManagementInfo(
      (result: any) => {
        if (!hasValue(result)) {
          setIsLoading(false);
          return;
        }

        setLastFourDigits(result.lastFourDigits ?? null);

        let balanceFormatted: string | null = null;
        let upcomingInvoiceBalanceFormatted: string | null = null;

        if (hasValue(result.customerBalance)) {
          balanceFormatted = getStripePriceObjectFormattedCurrency(result.customerBalance);
        }
        if (hasValue(result.upcomingInvoiceBalance)) {
          upcomingInvoiceBalanceFormatted = getStripePriceObjectFormattedCurrency(result.upcomingInvoiceBalance);
        }

        setCustomerBalance(hasValue(balanceFormatted) ? balanceFormatted : '-');
        setUpcomingInvoiceBalance(hasValue(upcomingInvoiceBalanceFormatted) ? upcomingInvoiceBalanceFormatted : '-');

        if (hasValue(result.subscriptionExpiration)) {
          setSubscriptionExpiration(
            isoDatetimeToFormattedDatetime(result.subscriptionExpiration, (window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT)
          );
          setSubscriptionExpirationRaw(result.subscriptionExpiration);
        }
        setCancelAtPeriodEnd(result.cancelAtPeriodEnd);
        setLastInvoiceUnpaidHostedUrl(result.lastInvoiceUnpaidHostedUrl || '');
        setIsLoading(false);
      },
      () => {
        App.showPopupNotification('Error getting default card information.', 'error');
        setIsLoading(false);
      }
    );
  }, []);

  const subscriptionExpired = useMemo(() => {
    if (hasValue(subscriptionExpirationRaw)) {
      return moment(subscriptionExpirationRaw).unix() < moment().unix();
    }
    return true;
  }, [subscriptionExpirationRaw]);

  const viewLastUnpaidInvoice = useCallback(() => {
    openWebpage(lastInvoiceUnpaidHostedUrl);
  }, [lastInvoiceUnpaidHostedUrl]);

  const renewSubscription = useCallback(() => {
    api.renewPaymentSubscription(
      () => {
        location.reload();
      },
      () => {
        App.showPopupNotification('Error renewing subscription.', 'error');
      }
    );
  }, []);

  const openCancelSubscriptionModal = useCallback(() => {
    setCancelSubscriptionModalOpen(true);
  }, []);

  const handleCancelSubscriptionConfirm = useCallback((data: { reason: string }) => {
    api.cancelPaymentSubscription(
      { reason: data.reason, isReasonSelected: false },
      () => {
        location.reload();
      },
      () => {
        App.showPopupNotification('Error cancelling subscription.', 'error');
      }
    );
    setCancelSubscriptionModalOpen(false);
  }, []);

  const handleCancelSubscriptionCancel = useCallback(() => {
    setCancelSubscriptionModalOpen(false);
  }, []);

  if (isLoading) {
    return <div id="PaymentManagementSection" className={theme} />;
  }

  return (
    <div id="PaymentManagementSection" className={theme}>
      <CancelSubscriptionModal
        open={cancelSubscriptionModalOpen}
        theme={theme}
        onConfirm={handleCancelSubscriptionConfirm}
        onCancel={handleCancelSubscriptionCancel}
      />

      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              {subscriptionExpired ? (
                <>
                  {/* Subscription Expired State */}
                  <div className="kst-page-header">
                    <h3 className="kst-page-title">Renew Orna</h3>
                  </div>

                  {lastInvoiceUnpaidHostedUrl ? (
                    <div className="kst-renew-section">
                      <p>Payment required for outstanding invoice to log into Orna. After payment has completed, please log out and in again.</p>
                      <button type="button" className="kstc-btn kstc-btn--primary" onClick={viewLastUnpaidInvoice}>
                        View Invoice
                      </button>
                    </div>
                  ) : (
                    <div className="kst-renew-section">
                      <p>Please contact support@kerkton.com to renew your subscription.</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Active Subscription State */}
                  <div className="kst-page-header">
                    <h3 className="kst-page-title">Account Information</h3>
                    <p className="kst-page-description kst-subscription-status">
                      {cancelAtPeriodEnd === true
                        ? `Subscription is not set to auto renew and will end ${subscriptionExpiration}.`
                        : cancelAtPeriodEnd === false
                          ? `Subscription is set to auto renew on ${subscriptionExpiration}.`
                          : 'No subscription.'}
                    </p>
                  </div>

                  <div className="kst-account-cards">
                    {/* Account Balance Card */}
                    <div className="kst-account-card">
                      <h4 className="kst-account-card-value">
                        {customerBalance !== null ? customerBalance : 'Balance not found.'}
                      </h4>
                      <p className="kst-account-card-label">Account Balance</p>
                      {!subscriptionExpired && cancelAtPeriodEnd === true && (
                        <button type="button" className="kstc-btn kstc-btn--primary" onClick={renewSubscription}>
                          Renew Subscription
                        </button>
                      )}
                      {!subscriptionExpired && cancelAtPeriodEnd === false && (
                        <button type="button" className="kstc-btn kstc-btn--danger" onClick={openCancelSubscriptionModal}>
                          Cancel Subscription
                        </button>
                      )}
                    </div>

                    {/* Upcoming Balance Card */}
                    <div className="kst-account-card">
                      <h4 className="kst-account-card-value">
                        {upcomingInvoiceBalance !== null ? upcomingInvoiceBalance : 'Upcoming balance not found.'}
                      </h4>
                      <p className="kst-account-card-label">Upcoming Balance</p>
                    </div>

                    {/* Card Info Card */}
                    <div className="kst-account-card">
                      <h4 className="kst-account-card-value">
                        {lastFourDigits !== null ? lastFourDigits : 'NO CARD'}
                      </h4>
                      <p className="kst-account-card-label">Last 4 Digits of Card</p>
                      <button
                        type="button"
                        id="showCardPaymentMethodUpdateDialog"
                        className="kstc-btn kstc-btn--primary"
                      >
                        Update Card
                      </button>
                    </div>
                  </div>

                  {/* Unpaid Invoice Warning */}
                  {lastInvoiceUnpaidHostedUrl && (
                    <div className="kst-unpaid-invoice-warning">
                      <p>Please pay your outstanding invoice to avoid an interruption in service.</p>
                      <button type="button" className="kstc-btn kstc-btn--primary" onClick={viewLastUnpaidInvoice}>
                        View Invoice
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Imperative Destroy
// ============================================================================

function destroyPaymentManagementScreen(): void {
  if (paymentManagementContainer) {
    destroyReactComponent(paymentManagementContainer);
    const parent = paymentManagementContainer.parentNode;
    if (parent) {
      const placeholder = document.createElement('div');
      placeholder.id = 'PaymentManagementSection';
      parent.replaceChild(placeholder, paymentManagementContainer);
    }
    paymentManagementContainer = null;
  }
  cardElement = null;
  stripe = null;
}

// ============================================================================
// Window Registration
// ============================================================================

(window as any).cleanUpPaymentManagement = () => destroyPaymentManagementScreen();

// ============================================================================
// Screen Registry
// ============================================================================

export { destroyPaymentManagementScreen };
